import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { type Role, type User, type WageCode } from "@avg/shared";
import { defaultReportingMonth } from "../lib/reportingWindow";
import { useMonthLabel, useT, type TranslateFn } from "../i18n/I18nContext";

type Tab = "users" | "wage-codes" | "export";

export function AdminPage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>("users");
  return (
    <div className="space-y-5 sm:space-y-6">
      <div>
        <h1 className="font-display text-2xl text-portal-primary sm:text-3xl">
          {t("admin.title")}
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          {t("admin.subtitle")}
        </p>
      </div>
      <div className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0">
        <TabBtn active={tab === "users"} onClick={() => setTab("users")}>
          {t("admin.tab.users")}
        </TabBtn>
        <TabBtn
          active={tab === "wage-codes"}
          onClick={() => setTab("wage-codes")}
        >
          {t("admin.tab.wageCodes")}
        </TabBtn>
        <TabBtn active={tab === "export"} onClick={() => setTab("export")}>
          {t("admin.tab.export")}
        </TabBtn>
      </div>
      {tab === "users" && <UsersTab t={t} />}
      {tab === "wage-codes" && <WageCodesTab t={t} />}
      {tab === "export" && <ExportTab t={t} />}
    </div>
  );
}

function TabBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition ${
        active
          ? "border-portal-primary text-portal-primary"
          : "border-transparent text-text-secondary hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

// === Users tab ===

function UsersTab({ t }: { t: TranslateFn }) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.listUsers(),
  });

  const users = usersQuery.data?.users ?? [];

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(f) ||
        u.email.toLowerCase().includes(f) ||
        (u.employeeId ?? "").includes(f),
    );
  }, [users, filter]);

  const patch = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) =>
      api.patchUser(id, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
    onError: (e: Error) => setError(e.message),
  });

  const create = useMutation({
    mutationFn: (data: {
      email: string;
      name: string;
      employeeId?: string | null;
      role?: Role;
      managerId?: string | null;
    }) => api.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setShowAdd(false);
    },
    onError: (e: Error) => setError(e.message),
  });

  const managers = useMemo(
    () =>
      users.filter(
        (u) => (u.role === "MANAGER" || u.role === "ADMIN") && u.active,
      ),
    [users],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input
            className="input w-full sm:w-72"
            placeholder={t("admin.users.search")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className="text-xs text-text-secondary">
            {t("admin.users.countOf", {
              filtered: filtered.length,
              total: users.length,
            })}
          </span>
        </div>
        <button
          className="btn-primary w-full sm:w-auto"
          onClick={() => setShowAdd(true)}
        >
          {t("admin.users.add")}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}{" "}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t("common.close")}
          </button>
        </div>
      )}

      {showAdd && (
        <AddUserForm
          managers={managers}
          onCancel={() => setShowAdd(false)}
          onSubmit={(d) => create.mutate(d)}
          busy={create.isPending}
          t={t}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-alt text-xs uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="px-4 py-3 text-left">{t("admin.users.col.name")}</th>
              <th className="px-4 py-3 text-left">
                {t("admin.users.col.employeeId")}
              </th>
              <th className="px-4 py-3 text-left">{t("admin.users.col.role")}</th>
              <th className="px-4 py-3 text-left">
                {t("admin.users.col.manager")}
              </th>
              <th className="px-4 py-3 text-center">
                {t("admin.users.col.active")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                managers={managers}
                onPatch={(data) => patch.mutate({ id: u.id, data })}
                t={t}
              />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-text-secondary"
                >
                  {t("admin.users.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserRow({
  user,
  managers,
  onPatch,
  t,
}: {
  user: User;
  managers: User[];
  onPatch: (data: Partial<User>) => void;
  t: TranslateFn;
}) {
  const [employeeId, setEmployeeId] = useState(user.employeeId ?? "");
  const isPlaceholder = user.email.endsWith("@avg.local");

  return (
    <tr className={isPlaceholder ? "bg-amber-50/40" : ""}>
      <td className="px-4 py-3">
        <div className="font-medium text-text-primary">{user.name}</div>
        <div className="text-xs text-text-secondary">
          {isPlaceholder ? (
            <span className="badge-warn">{t("admin.users.notLinked")}</span>
          ) : (
            user.email
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            className="input w-28"
            value={employeeId}
            placeholder={t("admin.users.employeeIdPlaceholder")}
            maxLength={6}
            onChange={(e) =>
              setEmployeeId(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            onBlur={() => {
              const next = employeeId || null;
              if (next !== (user.employeeId ?? null)) {
                if (next && next.length !== 6) return;
                onPatch({ employeeId: next });
              }
            }}
          />
        </div>
      </td>
      <td className="px-4 py-3">
        <select
          className="input w-32"
          value={user.role}
          onChange={(e) => onPatch({ role: e.target.value as Role })}
        >
          <option value="EMPLOYEE">{t("role.employee")}</option>
          <option value="MANAGER">{t("role.manager")}</option>
          <option value="ADMIN">{t("role.admin")}</option>
        </select>
      </td>
      <td className="px-4 py-3">
        <select
          className="input w-56"
          value={user.managerId ?? ""}
          onChange={(e) => onPatch({ managerId: e.target.value || null })}
        >
          <option value="">{t("admin.users.noManager")}</option>
          {managers
            .filter((m) => m.id !== user.id)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
        </select>
      </td>
      <td className="px-4 py-3 text-center">
        <input
          type="checkbox"
          checked={user.active}
          onChange={(e) => onPatch({ active: e.target.checked })}
          className="h-4 w-4 accent-portal-primary"
        />
      </td>
    </tr>
  );
}

function AddUserForm({
  managers,
  onCancel,
  onSubmit,
  busy,
  t,
}: {
  managers: User[];
  onCancel: () => void;
  onSubmit: (data: {
    email: string;
    name: string;
    employeeId?: string | null;
    role?: Role;
    managerId?: string | null;
  }) => void;
  busy: boolean;
  t: TranslateFn;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [role, setRole] = useState<Role>("EMPLOYEE");
  const [managerId, setManagerId] = useState<string>("");

  const valid =
    email.includes("@") &&
    name.trim() &&
    (!employeeId || employeeId.length === 6);

  return (
    <div className="card border-portal-primary p-5">
      <h3 className="text-base font-semibold text-text-primary">
        {t("admin.users.addTitle")}
      </h3>
      <p className="mt-1 text-xs text-text-secondary">
        {t("admin.users.addHelp")}
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="label">{t("admin.users.field.email")}</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("admin.users.field.emailPh")}
          />
        </div>
        <div>
          <label className="label">{t("admin.users.field.name")}</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("admin.users.field.namePh")}
          />
        </div>
        <div>
          <label className="label">
            {t("admin.users.field.employeeId")}
          </label>
          <input
            className="input"
            value={employeeId}
            maxLength={6}
            onChange={(e) =>
              setEmployeeId(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder={t("admin.users.field.employeeIdPh")}
          />
        </div>
        <div>
          <label className="label">{t("admin.users.field.role")}</label>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="EMPLOYEE">{t("role.employee")}</option>
            <option value="MANAGER">{t("role.manager")}</option>
            <option value="ADMIN">{t("role.admin")}</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">{t("admin.users.field.manager")}</label>
          <select
            className="input"
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
          >
            <option value="">{t("admin.users.noManager")}</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button
          className="btn-primary"
          disabled={!valid || busy}
          onClick={() =>
            onSubmit({
              email: email.trim().toLowerCase(),
              name: name.trim(),
              employeeId: employeeId || null,
              role,
              managerId: managerId || null,
            })
          }
        >
          {t("common.add")}
        </button>
      </div>
    </div>
  );
}

// === Wage codes tab ===

function WageCodesTab({ t }: { t: TranslateFn }) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wcQuery = useQuery({
    queryKey: ["admin-wage-codes"],
    queryFn: () => api.listAllWageCodes(),
  });

  const patch = useMutation({
    mutationFn: ({ code, data }: { code: string; data: Partial<WageCode> }) =>
      api.patchWageCode(code, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-wage-codes"] }),
    onError: (e: Error) => setError(e.message),
  });
  const create = useMutation({
    mutationFn: (wc: WageCode) => api.createWageCode(wc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-wage-codes"] });
      setShowAdd(false);
    },
    onError: (e: Error) => setError(e.message),
  });

  const codes = wcQuery.data?.wageCodes ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {t("admin.wageCodes.subtitle")}
        </p>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>
          {t("admin.wageCodes.add")}
        </button>
      </div>
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}{" "}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t("common.close")}
          </button>
        </div>
      )}
      {showAdd && (
        <AddWageCodeForm
          onCancel={() => setShowAdd(false)}
          onSubmit={(wc) => create.mutate(wc)}
          busy={create.isPending}
          t={t}
        />
      )}
      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-alt text-xs uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="px-4 py-3 text-left">{t("admin.wageCodes.col.code")}</th>
              <th className="px-4 py-3 text-left">
                {t("admin.wageCodes.col.description")}
              </th>
              <th className="px-4 py-3 text-center">
                {t("admin.wageCodes.col.requiresHours")}
              </th>
              <th className="px-4 py-3 text-center">
                {t("admin.wageCodes.col.active")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {codes.map((wc) => (
              <tr key={wc.code}>
                <td className="px-4 py-3 font-mono text-xs">{wc.code}</td>
                <td className="px-4 py-3">
                  <input
                    className="input"
                    defaultValue={wc.description}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== wc.description)
                        patch.mutate({
                          code: wc.code,
                          data: { description: v },
                        });
                    }}
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={wc.requiresHours}
                    onChange={(e) =>
                      patch.mutate({
                        code: wc.code,
                        data: { requiresHours: e.target.checked },
                      })
                    }
                    className="h-4 w-4 accent-portal-primary"
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={wc.active}
                    onChange={(e) =>
                      patch.mutate({
                        code: wc.code,
                        data: { active: e.target.checked },
                      })
                    }
                    className="h-4 w-4 accent-portal-primary"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddWageCodeForm({
  onCancel,
  onSubmit,
  busy,
  t,
}: {
  onCancel: () => void;
  onSubmit: (wc: WageCode) => void;
  busy: boolean;
  t: TranslateFn;
}) {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [requiresHours, setRequiresHours] = useState(false);
  return (
    <div className="card border-portal-primary p-5">
      <h3 className="text-base font-semibold text-text-primary">
        {t("admin.wageCodes.addTitle")}
      </h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="label">{t("admin.wageCodes.field.code")}</label>
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
            placeholder={t("admin.wageCodes.field.codePh")}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">
            {t("admin.wageCodes.field.description")}
          </label>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("admin.wageCodes.field.descriptionPh")}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={requiresHours}
            onChange={(e) => setRequiresHours(e.target.checked)}
            className="h-4 w-4 accent-portal-primary"
          />
          {t("admin.wageCodes.field.requiresHours")}
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button
          className="btn-primary"
          disabled={!code.trim() || !description.trim() || busy}
          onClick={() =>
            onSubmit({
              code: code.trim(),
              description: description.trim(),
              requiresHours,
              active: true,
              sortOrder: 100,
            })
          }
        >
          {t("common.save")}
        </button>
      </div>
    </div>
  );
}

// === Export tab ===

function ExportTab({ t }: { t: TranslateFn }) {
  const monthLabel = useMonthLabel();
  const def = defaultReportingMonth();
  const [year, setYear] = useState(def.year);
  const [month, setMonth] = useState(def.month);
  const [includeAll, setIncludeAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const teamQuery = useQuery({
    queryKey: ["team-reports", year, month],
    queryFn: () => api.getTeamReports(year, month),
  });
  const reports = teamQuery.data?.reports ?? [];

  const counts = {
    pending: reports.filter((r) => r.status === "SUBMITTED").length,
    managerOk: reports.filter((r) => r.status === "MANAGER_APPROVED").length,
    final: reports.filter((r) => r.status === "ADMIN_APPROVED").length,
    draft: reports.filter((r) => r.status === "DRAFT").length,
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const statuses = includeAll
        ? ["DRAFT", "SUBMITTED", "MANAGER_APPROVED", "ADMIN_APPROVED"]
        : ["ADMIN_APPROVED"];
      await api.downloadExport(year, month, statuses);
    } catch (err: any) {
      setError(err?.message ?? t("common.something_went_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <h3 className="text-base font-semibold text-text-primary">
          {t("admin.export.title")}
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          {t("admin.export.subtitle")}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="label">{t("admin.export.year")}</label>
            <input
              type="number"
              className="input w-28"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">{t("admin.export.month")}</label>
            <select
              className="input w-44"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              className="h-4 w-4 accent-portal-primary"
            />
            {t("admin.export.includeAll")}
          </label>
          <button
            className="btn-primary ml-auto"
            onClick={download}
            disabled={busy}
          >
            {busy ? t("admin.export.creating") : t("admin.export.download")}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Mini label={t("admin.export.stat.draft")} value={counts.draft} />
          <Mini label={t("admin.export.stat.pending")} value={counts.pending} />
          <Mini
            label={t("admin.export.stat.managerOk")}
            value={counts.managerOk}
          />
          <Mini label={t("admin.export.stat.final")} value={counts.final} />
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="font-display text-xl text-portal-primary">{value}</div>
    </div>
  );
}
